import React from 'react'
import { toast } from 'react-toastify'

import SelectPendencyStatus from '../SelectPendencyStatus'
import TextArea from 'presentation/shared/components/TextArea'
import Button from 'presentation/shared/components/Button'
import Accordion from 'presentation/shared/components/Accordion'
import Identifications from './Identifications'
import { DoctorDocument } from 'common/enum/doctor-document'
import Certifications from './Certifications'
import { Document } from 'domain/entities/document'

import * as S from './styles'
import { useFormik } from 'formik'
import { DoctorRegistrationSteps } from 'common/enum/doctor-overall-registration-steps'
import { DoctorRegisterStepStatus } from 'domain/entities/doctor-register'
import {
  disableObservation,
  generateNewRegistrationDetailsValidationSchema
} from 'presentation/hospital/layouts/DoctorRegister/NewRegistrationsDetails'
import Resume from 'presentation/hospital/components/DoctorRegister/Details/Documents/Resume'
import { newRegistrationDetailsContext } from 'presentation/hospital/layouts/DoctorRegister/NewRegistrationsDetails/context'

type DocumentsProps = {
  documents: Document[]
  identificationsData?: {
    rg?: string
    cpf?: string
  }
  doctor_id?: number
  initialValues: registerPendencyValues
  updateStepStatus?: (step: any, initialValues: any) => Promise<void>
  isDirector: boolean
}

const Documents = ({
  documents,
  identificationsData,
  doctor_id,
  initialValues,
  updateStepStatus,
  isDirector
}: DocumentsProps) => {
  const { isStepsUpdateBlocked } = newRegistrationDetailsContext
  const { handleChange, values, setFieldValue, handleSubmit, isValid } =
    useFormik({
      initialValues: initialValues,
      validateOnMount: true,
      validationSchema: generateNewRegistrationDetailsValidationSchema(
        DoctorRegistrationSteps.DOCUMENTS_AND_ATTACHMENTS
      ),
      onSubmit: async (values) => {
        try {
          updateStepStatus?.(values, initialValues)
          toast.success('Dados enviados com sucesso.')
          window.scrollTo(0, 0)
        } catch (error) {
          toast.error('Erro ao salvar dados.')
          return
        }
      }
    })

  const getIdentificationsDocuments = () => {
    return documents.filter(isIdentificationDocument)
  }

  const getCertificationsDocuments = () => {
    return documents.filter(isCertificationDocument)
  }

  const isIdentificationDocument = (document: Document) => {
    const identificationDocuments = [
      DoctorDocument.DRIVERS_LICENSE_FRONT,
      DoctorDocument.DRIVERS_LICENSE_BACK,
      DoctorDocument.IDENTITY_CARD_FRONT,
      DoctorDocument.IDENTITY_CARD_BACK,
      DoctorDocument.INDIVIDUAL_REGISTRATION_FRONT,
      DoctorDocument.INDIVIDUAL_REGISTRATION_BACK,
      DoctorDocument.BADGE_PHOTO,
      DoctorDocument.ADVICE_WALLET
    ]

    return identificationDocuments.some((item) => item === document.type)
  }

  const isCertificationDocument = (document: Document) => {
    const certificationDocuments = [
      DoctorDocument.PALS,
      DoctorDocument.NALS,
      DoctorDocument.ACLS,
      DoctorDocument.OTHER_DOCTOR_CERTIFICATES,
      DoctorDocument.SPECIALIZATION_DEGREE,
      DoctorDocument.UNDERGRADUATE_DEGREE_FRONT,
      DoctorDocument.UNDERGRADUATE_DEGREE_BACK,
      DoctorDocument.CLINIC_INTERN_PROGRAM,
      DoctorDocument.COUNCIL_ANNUITY_PAYMENT
    ]

    return certificationDocuments.some((item) => item === document.type)
  }

  const setPendencyStatus = (field: string, event: any) => {
    if (isDirector) {
      setFieldValue(
        `${field}.status`,
        event.target.value === 'Revisão'
          ? DoctorRegisterStepStatus.REVIEW
          : DoctorRegisterStepStatus.APPROVED_DIRECTOR
      )
    } else {
      setFieldValue(
        `${field}.status`,
        event.target.value === 'Pendente'
          ? DoctorRegisterStepStatus.PENDENT
          : DoctorRegisterStepStatus.APPROVED_DOCTOR_REGISTER
      )
    }
  }

  const getResumeDocument = () => {
    return documents.find((document) => document.type === DoctorDocument.LATTES)
  }

  const documentItems = [
    {
      title: 'Documentos de identificação',
      content: (
        <Identifications
          rg={identificationsData?.rg}
          cpf={identificationsData?.cpf}
          doctor_id={doctor_id!}
          documents={getIdentificationsDocuments()}
        />
      )
    },
    {
      title: 'Comprovantes e certificados',
      content: (
        <Certifications
          certifications={getCertificationsDocuments()}
          doctor_id={doctor_id!}
        />
      )
    },
    {
      title: 'Currículo',
      content: <Resume resume={getResumeDocument()} doctor_id={doctor_id!} />
    }
  ]

  return (
    <S.Wrapper data-testid="card-documents">
      <S.PendencySelection>
        <SelectPendencyStatus
          handleChange={(e) =>
            setPendencyStatus(
              DoctorRegistrationSteps.DOCUMENTS_AND_ATTACHMENTS,
              e
            )
          }
          initialValue={
            values[DoctorRegistrationSteps.DOCUMENTS_AND_ATTACHMENTS].status
          }
          isDirector={isDirector}
          disabled={isStepsUpdateBlocked}
        />
      </S.PendencySelection>
      <S.Title>Documentos e anexos</S.Title>
      <S.DocumentsContainer>
        <Accordion items={documentItems} />
        <S.DirectorObservations>
          <TextArea
            label="Observações do Diretor"
            name={`${DoctorRegistrationSteps.DOCUMENTS_AND_ATTACHMENTS}.director_review`}
            value={
              values[DoctorRegistrationSteps.DOCUMENTS_AND_ATTACHMENTS]
                .director_review
            }
            onChange={handleChange}
            required
            disabled={
              !isDirector ||
              values[DoctorRegistrationSteps.DOCUMENTS_AND_ATTACHMENTS]
                .status !== DoctorRegisterStepStatus.REVIEW
            }
            data-testid="input-diretor-observation-documents"
          />
        </S.DirectorObservations>
        {!isDirector && (
          <S.PendencyJustification>
            <TextArea
              name={`${DoctorRegistrationSteps.DOCUMENTS_AND_ATTACHMENTS}.description`}
              label="No caso de pendência, favor descrever"
              value={
                values[DoctorRegistrationSteps.DOCUMENTS_AND_ATTACHMENTS]
                  .description
              }
              onChange={handleChange}
              required
              data-testid="input-pendency-documents"
              disabled={
                disableObservation(
                  DoctorRegistrationSteps.DOCUMENTS_AND_ATTACHMENTS
                ) ||
                values[DoctorRegistrationSteps.DOCUMENTS_AND_ATTACHMENTS]
                  .status !== DoctorRegisterStepStatus.PENDENT ||
                isStepsUpdateBlocked
              }
            />
          </S.PendencyJustification>
        )}
      </S.DocumentsContainer>
      <S.ActionsButtons>
        <Button
          data-testid="btn-save-documents"
          onClick={() => handleSubmit()}
          variant={isValid && !isStepsUpdateBlocked ? 'outlined' : undefined}
          disabled={!isValid || isStepsUpdateBlocked}
        >
          Salvar
        </Button>
      </S.ActionsButtons>
    </S.Wrapper>
  )
}

type registerPendencyValues = {
  [DoctorRegistrationSteps.DOCUMENTS_AND_ATTACHMENTS]: {
    description?: string
    director_review?: string
    status: DoctorRegisterStepStatus
  }
}

export default Documents
